import React, { useEffect, useState } from 'react'
import CtaLink from 'components/ui/CtaLink'
import TrucksSection from 'components/pages/CateringMarketPage/TrucksSection'
import { marketCateringQuery_marketBySlug } from 'components/pages/CateringMarketPage/types/marketCateringQuery'
import cn from 'classnames'
import { ClientLogos } from 'components/ClientClouds'

type Props = {
  state: string
  market: marketCateringQuery_marketBySlug
  isCateringPage?: boolean
}

const RotatingHeader = () => {
  const content = [
    {
      title: 'Fresh',
      className: 'text-green-500',
      description:
        'We cook in front of you, while traditional catering arrives cold & soggy'
    },
    {
      title: 'Affordable',
      className: 'text-blue-600',
      description:
        'Food truck catering is more cost-effective than traditional catering'
    },
    {
      title: 'Varied',
      className: 'text-orange-600',
      description: 'We have a food truck for every occasion and cuisine'
    }
  ]

  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % content.length)
    }, 4000)

    return () => clearInterval(intervalId)
  }, [content.length])

  return (
    <div className="flex items-center justify-center bg-gray-100 pb-4 px-6 -mx-4 md:bg-transparent md:p-0">
      <div className="text-center w-full">
        <div className="relative h-16 md:mb-2">
          {content.map((item, index) => (
            <h1
              key={item.title}
              className={cn(
                `
                absolute inset-0 flex items-center justify-center 
                text-4xl md:text-6xl font-bold
                transition-all duration-700 ease-in-out transform
              `,
                {
                  'opacity-100 delay-200 ': index === currentIndex,
                  'opacity-0': index !== currentIndex
                },
                item.className
              )}
            >
              {item.title}
            </h1>
          ))}
        </div>

        <div className="relative h-12">
          {content.map((item, index) => (
            <p
              key={item.title}
              className={cn(
                `
                absolute inset-0 flex items-center justify-center
                text-base md:text-lg text-gray-600
                italic
                transition-all duration-700 ease-in-out transform
              `,
                {
                  'opacity-100 delay-200': index === currentIndex,
                  'opacity-0': index !== currentIndex
                }
              )}
            >
              {item.description}
            </p>
          ))}
        </div>

        <div className="h-2 md:hidden flex flex-row gap-0.5 justify-center items-center mt-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                'rounded-full transition-all duration-700 ease-in-out transform',
                {
                  'w-1.5 h-1.5 bg-gray-300': i === currentIndex,
                  'w-1 h-1 bg-gray-300': i !== currentIndex
                }
              )}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const HeroSection = ({ state, market, isCateringPage = false }: Props) => {
  return (
    <div>
      <div className="mx-auto max-w-7xl pt-4 md:pt-6 text-center lg:text-left px-2 md:px-8">
        <h1 className="text-4xl text-center tracking-tight font-extrabold text-gray-900 sm:text-5xl xl:text-6xl">
          <span className="block xl:inline">
            {isCateringPage ? 'Food Truck Catering in' : 'Food Trucks in'}
          </span>{' '}
          <span className="block text-orange-500 xl:inline">
            {market.name}, {state}
          </span>
        </h1>
        <div className="md:grid grid-cols-3 gap-x-2 md:mt-10 mt-4">
          <div className="col-span-1 text-gray-900 sm:text-xl rounded-xl flex justify-center items-center flex-col text-center md:-mt-20">
            <div className="mb-2 md:mb-8 w-full">
              <RotatingHeader />
            </div>

            <div className="mt-2 md:mt-0">
              <div>
                <p className="text-base sm:text-lg font-light">
                  Catering starts from
                </p>
                <p className="order-first text-4xl md:text-7xl tracking-tight font-bold">
                  {market.cateringMinBudget.formatted.replace(',', '')}
                </p>
              </div>

              <div className="text-gray-800 font-light italic mt-1 md:mt-4 text-sm md:text-base text-center">
                <p className="underline">On a budget?</p>
                <p>
                  Drop off prices start from{' '}
                  {market.cateringDropOffMinBudget.formatted}
                </p>
              </div>

              <div className="mt-4 md:mt-12 flex flex-col-reverse gap-4 md:flex-col">
                <p className="max-w-md mx-auto md:mx-0 px-6 md:px-0 sm:text-xl md:max-w-sm font-medium">
                  Submit a request now to get a free quote with a list of
                  available trucks
                </p>
                <div className="max-w-max mx-auto">
                  <CtaLink href="/hire-food-truck" prefetch={false}>
                    <p className="whitespace-nowrap">Get a free quote</p>
                  </CtaLink>
                </div>
              </div>
            </div>
          </div>

          <div className="md:mt-0 mt-4 col-span-2">
            <TrucksSection marketId={market.id} marketName={market.name} />
          </div>
        </div>
      </div>

      <ClientLogos
        className="bg-white border-t-2 border-b-2 border-solid border-orange-300"
        clients={[
          'pfizer',
          'boeing',
          'apple',
          'amazon',
          'cushman',
          'aramark',
          'compass',
          'swedish'
        ]}
      />
    </div>
  )
}

export default HeroSection
